import { useCallback, useMemo } from 'react'

import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { StyleSheet } from 'react-native'

import { ViewStyle, TextStyle } from 'react-native'
import { dsv } from '../styles/defaults'

export interface SelectListProps<TOptionKey extends string = string> {
  /**
   * Fn to set Selected option value which will be stored in your local state
   */
  setSelected: (selectedKey: TOptionKey | undefined) => void

  /**
   * Placeholder text that will be displayed in the select box
   */
  placeholder?: string

  /**
   * Additional styles for select box
   */
  boxStyles?: ViewStyle

  /**
   *  	Additional styles for text of select box
   */
  inputStyles?: TextStyle

  /**
   *  	Additional styles for dropdown scrollview
   */
  dropdownStyles?: ViewStyle

  /**
   *  Additional styles for dropdown list item
   */
  dropdownItemStyles?: ViewStyle

  /**
   * Additional styles for list items text
   */
  dropdownTextStyles?: TextStyle

  /**
   * Maximum height of the dropdown wrapper to occupy
   */
  maxHeight?: number

  /**
   * Data which will be iterated as options of select list
   */
  readonly data: Array<{ key: TOptionKey; value: string; disabled?: boolean }>

  disabled?: boolean

  /**
   * The default option of the select list
   */
  defaultOption?: { key: TOptionKey; value: string }

  /**
   * Pass any JSX to this prop like Text, Image or Icon to show instead of search icon
   */
  searchicon?: JSX.Element

  /**
   *  Pass any JSX to this prop like Text, Image or Icon to show instead of chevron icon
   */
  arrowicon?: JSX.Element

  /**
   * Kept for compatibility. SelectList is intentionally selection-only and does not allow text input.
   */
  search?: boolean

  /**
   * Kept for compatibility. SelectList is intentionally selection-only and does not allow text input.
   */
  searchPlaceholder?: string

  /**
   * Trigger an action when option is selected
   */
  onSelect?: () => void

  /**
   * set fontFamily of whole component Text
   */
  fontFamily?: string

  /**
   * set this to change the default search failure text
   */
  notFoundText?: string

  /**
   * Additional styles for disabled list item
   */
  disabledItemStyles?: ViewStyle

  /**
   * Additional styles for disabled list items text
   */
  disabledTextStyles?: TextStyle

  /**
   * What to store inside your local state (key or value)
   */
  save?: 'key' | 'value'

  /**
   * Control the dropdown with this prop
   */
  dropdownShown?: boolean

  /**
   *  Pass any JSX to this prop like Text, Image or Icon to show instead of close icon
   */
  closeicon?: JSX.Element
}

type SelectListOption<TOptionKey extends string = string> = {
  key: TOptionKey
  value: string
  disabled?: boolean
}

function SelectList<TOptionKey extends string = string>({
  setSelected,
  placeholder,
  boxStyles,
  inputStyles,
  dropdownStyles,
  dropdownItemStyles,
  dropdownTextStyles,
  maxHeight = 240,
  data,
  defaultOption,
  disabledItemStyles,
  disabledTextStyles,
  onSelect = () => { },
  save = 'key',
  fontFamily,
  disabled,
}: SelectListProps<TOptionKey>) {
  const flattenedBoxStyles = useMemo(() => StyleSheet.flatten(boxStyles) ?? {}, [boxStyles])
  const flattenedInputStyles = useMemo(() => StyleSheet.flatten(inputStyles) ?? {}, [inputStyles])
  const flattenedDropdownStyles = useMemo(() => StyleSheet.flatten(dropdownStyles) ?? {}, [dropdownStyles])
  const flattenedDropdownItemStyles = useMemo(
    () => StyleSheet.flatten(dropdownItemStyles) ?? {},
    [dropdownItemStyles]
  )
  const flattenedDropdownTextStyles = useMemo(
    () => StyleSheet.flatten(dropdownTextStyles) ?? {},
    [dropdownTextStyles]
  )
  const flattenedDisabledItemStyles = useMemo(() => StyleSheet.flatten(disabledItemStyles) ?? {}, [disabledItemStyles])
  const flattenedDisabledTextStyles = useMemo(() => StyleSheet.flatten(disabledTextStyles) ?? {}, [disabledTextStyles])

  const selectedKey = useMemo(() => defaultOption?.key ?? '', [defaultOption?.key])
  const optionByKey = useMemo(
    () => new Map<TOptionKey, SelectListOption<TOptionKey>>(data.map((option) => [option.key, option])),
    [data]
  )

  const selectOption = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedValue = event.target.value

      if (selectedValue === '') {
        setSelected(undefined)
        onSelect()
        return
      }

      const option = optionByKey.get(selectedValue as TOptionKey)
      if (option == null) {
        return
      }

      if (save === 'value') {
        setSelected(option.value as TOptionKey)
      } else {
        setSelected(option.key)
      }
      onSelect()
    },
    [onSelect, optionByKey, save, setSelected]
  )

  return (
    <Select
      disabled={disabled}
      displayEmpty
      MenuProps={{
        PaperProps: {
          sx: {
            ...flattenedDropdownStyles,
            maxHeight,
            border: `1px solid ${dsv.colors.connectGrau30}`,
            boxShadow: '0 8px 18px rgba(0, 0, 0, 0.16)',
          },
        },
      }}
      onChange={selectOption}
      renderValue={(selected) => {
        if (selected === '') {
          return placeholder ?? 'Option auswählen'
        }

        return optionByKey.get(selected as TOptionKey)?.value ?? selected
      }}
      sx={{
        width: '100%',
        height: flattenedBoxStyles.height ?? flattenedBoxStyles.minHeight ?? 36,
        minHeight: flattenedBoxStyles.minHeight ?? 36,
        backgroundColor: flattenedBoxStyles.backgroundColor ?? dsv.colors.base,
        borderRadius: flattenedBoxStyles.borderRadius ?? 1,
        color: flattenedInputStyles.color ?? dsv.colors.neutral,
        fontFamily: fontFamily ?? flattenedInputStyles.fontFamily ?? dsv.text.fontFamily,
        fontSize: flattenedInputStyles.fontSize ?? 14,
        ...flattenedBoxStyles,
        '.MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          minHeight: 'auto',
          padding: '6px 32px 6px 12px',
          color: selectedKey === '' ? dsv.colors.connectGrau50 : undefined,
          ...flattenedInputStyles,
          fontFamily: fontFamily ?? flattenedInputStyles.fontFamily ?? dsv.text.fontFamily,
          fontSize: flattenedInputStyles.fontSize ?? 14,
        },
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: flattenedBoxStyles.borderColor ?? dsv.colors.connectGrau30,
        },
      }}
      value={selectedKey}
    >
      <MenuItem disabled value="" sx={{ fontSize: 14 }}>
        {placeholder ?? 'Option auswählen'}
      </MenuItem>
      {data.length === 0 ? (
        <MenuItem disabled value="__empty" sx={{ fontSize: 14 }}>
          Keine Optionen vorhanden
        </MenuItem>
      ) : data.map((item) => (
        <MenuItem
          disabled={item.disabled === true}
          key={item.key}
          value={item.key}
          sx={{
            ...flattenedDropdownItemStyles,
            ...flattenedDropdownTextStyles,
            fontSize: flattenedDropdownTextStyles.fontSize ?? 14,
            minHeight: 34,
            '&.Mui-disabled': {
              ...flattenedDisabledItemStyles,
              ...flattenedDisabledTextStyles,
              opacity: 0.7,
            },
          }}
        >
          {item.value}
        </MenuItem>
      ))}
    </Select>
  )
}

export default SelectList
